import { BOT_PROFILES, BOT_STATE_META, getMarketBySymbol } from "./marketCatalog";
import { formatCountdown, intervalToSeconds } from "./indicators";

const BASE_PORTFOLIO_BALANCE = Number(import.meta.env.VITE_DEFAULT_TRADING_BALANCE || 0);

export const createExecutionLog = (message, tone = "info") => ({
  id: crypto.randomUUID(),
  timestamp: new Date().toISOString(),
  tone,
  message,
});

export const createTradeMarker = ({ time, position, color, shape, text }) => ({
  time,
  position,
  color,
  shape,
  text,
});

export const createInitialExecutionRuntime = (settings) => ({
  state: "stopped",
  simulationStartedAt: null,
  startingBalance: BASE_PORTFOLIO_BALANCE,
  sessionStake: settings.tradeAmount,
  currentBalance: BASE_PORTFOLIO_BALANCE,
  equity: BASE_PORTFOLIO_BALANCE,
  activeTradingAmount: 0,
  cycleDuration: settings.cycleDuration,
  cycleMs: intervalToSeconds(settings.cycleDuration) * 1000,
  nextTradeAt: null,
  pausedRemainingMs: null,
  sessionsCompleted: 0,
  totalProfitLoss: 0,
  realizedProfitLoss: 0,
  winRate: 0,
  wins: 0,
  losses: 0,
  activeTradeStatus: "No active trade",
  currentBotMode: settings.mode,
  botSelection: settings.botSelection,
  openPosition: null,
  logs: [createExecutionLog("Execution engine ready. Configure a bot and start trading.")],
  markers: [],
  lastRsi: 50,
  lastConfidence: 0,
  tradeAttempts: 0,
  backendPulse: 0,
  latestSummary: "No trading session completed yet.",
  roiPercent: 0,
  lastClosedTradePnl: 0,
  lastTradeStake: 0,
  lastTradeValue: 0,
  outcomePlan: [],
});

export const getBotProfile = (botSelection) =>
  BOT_PROFILES[botSelection] || BOT_PROFILES.lowRisk;

export const calculateConfidence = ({ profile, rsi, priceDrift }) => {
  const oversoldBoost = Math.max(0, profile.entryRsi - rsi) * 2.2;
  const momentumBoost = Math.max(0, Math.abs(priceDrift)) * 8;
  return Math.min(96, Math.max(18, profile.confidenceBias + oversoldBoost + momentumBoost));
};

export const createScanLogs = ({ marketLabel, rsi, confidence }) => {
  return [
    createExecutionLog(`Monitoring ${marketLabel} market conditions...`, "info"),
    createExecutionLog(`RSI evaluation completed at ${rsi.toFixed(2)}.`, rsi < 30 ? "warn" : "info"),
    createExecutionLog(
      `Liquidity, volatility, and support/resistance checks complete. Confidence ${confidence.toFixed(1)}%.`,
      confidence >= 70 ? "success" : "info"
    ),
  ];
};

export const syncRuntimeWithMarket = ({ runtime, marketPrice }) => {
  if (!runtime.openPosition) {
    return {
      ...runtime,
      equity: runtime.currentBalance,
      activeTradingAmount: 0,
      totalProfitLoss: runtime.realizedProfitLoss,
      lastTradeValue: runtime.lastTradeValue || runtime.lastTradeStake || 0,
    };
  }

  const marketValue = runtime.openPosition.quantity * marketPrice;
  const unrealizedPnL = marketValue - runtime.openPosition.invested;
  const totalProfitLoss = runtime.realizedProfitLoss + unrealizedPnL;

  return {
    ...runtime,
    equity: runtime.currentBalance + unrealizedPnL,
    activeTradingAmount: runtime.openPosition.invested,
    totalProfitLoss,
    lastTradeStake: runtime.openPosition.invested,
    lastTradeValue: marketValue,
    activeTradeStatus: `${runtime.openPosition.side.toUpperCase()} ${runtime.openPosition.marketLabel} | ${unrealizedPnL >= 0 ? "+" : "-"}$${Math.abs(unrealizedPnL).toFixed(2)}`,
  };
};

export const buildPassiveActivity = ({ runtime, rsi, confidence, marketLabel, marketPrice }) => {
  if (runtime.state === "paused" || runtime.state === "stopped") {
    return { runtime, logs: [] };
  }

  const nextRuntime = {
    ...runtime,
    backendPulse: runtime.backendPulse + 1,
    lastRsi: rsi,
    lastConfidence: confidence,
  };

  const logs = [];

  if (runtime.openPosition) {
    const livePnL =
      runtime.openPosition.quantity * marketPrice - runtime.openPosition.invested;
    logs.push(
      createExecutionLog(
        `Execution position now ${livePnL >= 0 ? "in" : "under"} ${livePnL >= 0 ? "+" : ""}${livePnL.toFixed(2)} USD. Confidence ${confidence.toFixed(1)}%.`,
        livePnL >= 0 ? "success" : "warn"
      )
    );
  } else {
    const scanningPhrases = [
      "Scanning momentum divergence...",
      "Checking volatility compression...",
      "Calculating trade confidence...",
      "Watching liquidity sweep zones...",
      "Evaluating support and resistance reaction...",
    ];

    logs.push(
      createExecutionLog(
        `${scanningPhrases[runtime.backendPulse % scanningPhrases.length]} ${marketLabel} @ ${marketPrice.toFixed(2)}.`,
        "info"
      )
    );
  }

  return { runtime: nextRuntime, logs };
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const createOutcomePlan = (wins = 7, losses = 3) => {
  const plan = [
    ...Array.from({ length: wins }, () => "win"),
    ...Array.from({ length: losses }, () => "loss"),
  ];

  for (let index = plan.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [plan[index], plan[swapIndex]] = [plan[swapIndex], plan[index]];
  }

  return plan;
};

const getOutcomeForTrade = (runtime) => {
  const closedTrades = runtime.sessionsCompleted;
  const tradeIndex = closedTrades % 10;
  const needsFreshPlan = !runtime.outcomePlan?.length || tradeIndex === 0;
  const plan = needsFreshPlan ? createOutcomePlan(7, 3) : runtime.outcomePlan;
  return {
    plan,
    outcome: plan[tradeIndex] || "win",
  };
};

const getSimulationProfile = (settings, runtime) => {
  const elapsedMs = runtime.simulationStartedAt
    ? Math.max(0, Date.now() - runtime.simulationStartedAt)
    : 0;
  const elapsedMinutes = elapsedMs / 60000;
  const profitSpeed = Number(settings.profitSpeed || 1);
  const volatility = Number(settings.volatility || 1);
  const maxRoi = Number(settings.maxRoi || 300);
  const tradeFrequency = Number(settings.tradeFrequency || 1);
  const growthRatio = clamp(
    (elapsedMinutes * (0.12 + profitSpeed * 0.08) * tradeFrequency) / 6,
    0,
    1
  );
  const targetRoi = maxRoi * growthRatio;
  const oscillation = Math.sin((runtime.tradeAttempts + 1) * 1.35) * volatility * 1.8;
  const noise = (Math.random() - 0.5) * volatility * 3.6;
  const drawdown = Math.random() < 0.42 ? -(volatility * (0.8 + Math.random() * 2.2)) : 0;
  const upwardBias = Math.max(0.45, (targetRoi - runtime.roiPercent) * 0.18);
  const cycleDeltaRoi = clamp(upwardBias + oscillation + noise + drawdown, -8.5, 16.5);

  return {
    elapsedMinutes,
    maxRoi,
    targetRoi,
    cycleDeltaRoi,
  };
};

export const runTradeCycle = ({
  runtime,
  settings,
  marketPrice,
  rsi,
  candleTime,
  priceDrift,
}) => {
  const profile = getBotProfile(settings.botSelection);
  const market = getMarketBySymbol(settings.pair);
  const confidence = calculateConfidence({ profile, rsi, priceDrift });
  const simulation = getSimulationProfile(settings, runtime);
  const nextRuntime = {
    ...runtime,
    lastRsi: rsi,
    lastConfidence: confidence,
    cycleDuration: settings.cycleDuration,
    cycleMs: intervalToSeconds(settings.cycleDuration) * 1000,
    botSelection: settings.botSelection,
    currentBotMode: settings.mode,
  };
  const events = [];
  const getExecutableTradeAmount = () =>
    Number(Math.max(0, Math.min(settings.tradeAmount, nextRuntime.currentBalance)).toFixed(2));

  nextRuntime.logs = [...runtime.logs, ...createScanLogs({ marketLabel: market.label, rsi, confidence })];

  if (runtime.state === "paused" || runtime.state === "stopped") {
    return { runtime: nextRuntime, events };
  }

  nextRuntime.tradeAttempts += 1;
  nextRuntime.nextTradeAt = Date.now() + nextRuntime.cycleMs;
  events.push({
    type: "transaction",
    transaction: {
      id: crypto.randomUUID(),
      symbol: settings.pair,
      side: "scan",
      status: "completed",
      quantity: null,
      price: marketPrice,
      profit: null,
      amount: 0,
      investedAmount: 0,
      source: settings.botSelection,
      timestamp: new Date().toISOString(),
      description: `Market scan on ${market.label}. RSI ${rsi.toFixed(2)}, confidence ${confidence.toFixed(1)}%.`,
    },
  });

  const executeBuy = (type, amount) => {
    const quantity = amount / marketPrice;
    const tradeId = crypto.randomUUID();
    const attemptId = crypto.randomUUID();
    nextRuntime.state = type === "dca" ? "executingDca" : "executingBuy";
    nextRuntime.logs.push(
      createExecutionLog(`${type === "dca" ? "DCA ENTRY ATTEMPT" : "BUY ATTEMPT"} detected on ${market.label}.`, "warn"),
      createExecutionLog(`${type === "dca" ? "EXECUTION EVENT DCA EXECUTED" : "EXECUTION EVENT BUY EXECUTED"} ${market.label} @ ${marketPrice.toFixed(2)}`, "success")
    );
    events.push({
      type: "transaction",
      transaction: {
        id: attemptId,
        symbol: settings.pair,
        side: type === "dca" ? "dca-attempt" : "buy-attempt",
        status: "completed",
        quantity,
        price: marketPrice,
        profit: null,
        amount,
        investedAmount: amount,
        source: settings.botSelection,
        timestamp: new Date().toISOString(),
        description: `${type === "dca" ? "DCA" : "Buy"} attempt detected on ${market.label}.`,
      },
    });

    if (!nextRuntime.openPosition) {
      nextRuntime.openPosition = {
        id: tradeId,
        side: "long",
        marketLabel: market.label,
        quantity,
        invested: amount,
        averageEntry: marketPrice,
        dcaCount: 0,
      };
    } else {
      const totalInvested = nextRuntime.openPosition.invested + amount;
      const totalQuantity = nextRuntime.openPosition.quantity + quantity;
      nextRuntime.openPosition = {
        ...nextRuntime.openPosition,
        quantity: totalQuantity,
        invested: totalInvested,
        averageEntry: totalInvested / totalQuantity,
        dcaCount: nextRuntime.openPosition.dcaCount + 1,
      };
    }
    events.push({
      type: "transaction",
      transaction: {
        id: tradeId,
        symbol: settings.pair,
        side: type === "dca" ? "dca-buy" : "buy",
        status: "completed",
        quantity,
        price: marketPrice,
        profit: 0,
        amount,
        investedAmount: amount,
        source: settings.botSelection,
        timestamp: new Date().toISOString(),
        description: `${type === "dca" ? "DCA" : "Buy"} executed on ${market.label} @ ${marketPrice.toFixed(2)}.`,
      },
    });
    nextRuntime.lastTradeStake = nextRuntime.openPosition.invested;
    nextRuntime.lastTradeValue = nextRuntime.openPosition.quantity * marketPrice;
    nextRuntime.markers = [
      ...nextRuntime.markers.slice(-59),
      createTradeMarker({
        time: candleTime,
        position: "belowBar",
        color: type === "dca" ? "#f59e0b" : "#22c55e",
        shape: "arrowUp",
        text: type === "dca" ? "DCA" : "BUY",
      }),
    ];
    nextRuntime.state = "monitoring";
  };

  const closePosition = (reason, tone) => {
    const closingTradeId = crypto.randomUUID();
    const { plan: outcomePlan, outcome: plannedOutcome } = getOutcomeForTrade(runtime);
    const investedBase = nextRuntime.openPosition.invested || settings.tradeAmount;
    let cycleProfit = (investedBase * simulation.cycleDeltaRoi) / 100;

    if (plannedOutcome === "win") {
      cycleProfit = Math.abs(cycleProfit) + investedBase * (0.002 + Math.random() * 0.02);
    }

    if (plannedOutcome === "loss") {
      cycleProfit = -Math.abs(cycleProfit) - investedBase * (0.0015 + Math.random() * 0.012);
    }

    const targetBalance = runtime.startingBalance * (1 + simulation.maxRoi / 100);
    const cappedProfit =
      cycleProfit >= 0
        ? Math.min(cycleProfit, Math.max(0, targetBalance - nextRuntime.currentBalance))
        : cycleProfit;
    const realizedPnL = Number(cappedProfit.toFixed(2));
    const signedPnl = `${realizedPnL >= 0 ? "+" : "-"}$${Math.abs(realizedPnL).toFixed(2)}`;

    nextRuntime.currentBalance = Number((nextRuntime.currentBalance + realizedPnL).toFixed(2));
    nextRuntime.realizedProfitLoss += realizedPnL;
    nextRuntime.sessionsCompleted += 1;
    nextRuntime.wins += realizedPnL >= 0 ? 1 : 0;
    nextRuntime.losses += realizedPnL < 0 ? 1 : 0;
    nextRuntime.winRate =
      nextRuntime.sessionsCompleted > 0
        ? (nextRuntime.wins / nextRuntime.sessionsCompleted) * 100
        : 0;
    nextRuntime.state = reason === "take-profit" ? "takingProfit" : "closed";
    nextRuntime.logs.push(
      createExecutionLog(
        `${reason === "take-profit" ? "EXECUTION EVENT SELL EXECUTED" : "EXECUTION EVENT TRADE CLOSED"} ${signedPnl}`,
        tone
      ),
      createExecutionLog("AI execution cycle completed successfully.", "success")
    );
    nextRuntime.latestSummary = `${market.label} ${reason} | ${signedPnl} execution event`;
    nextRuntime.roiPercent = Number(
      (((nextRuntime.currentBalance - runtime.startingBalance) / runtime.startingBalance) * 100).toFixed(2)
    );
    nextRuntime.lastClosedTradePnl = realizedPnL;
    nextRuntime.lastTradeStake = nextRuntime.openPosition.invested;
    nextRuntime.lastTradeValue = Number(
      (nextRuntime.openPosition.invested + realizedPnL).toFixed(2)
    );
    nextRuntime.outcomePlan = outcomePlan;

    nextRuntime.markers = [
      ...nextRuntime.markers.slice(-59),
      createTradeMarker({
        time: candleTime,
        position: "aboveBar",
        color: realizedPnL >= 0 ? "#22c55e" : "#ef4444",
        shape: "arrowDown",
        text: realizedPnL >= 0 ? `TP +$${Math.abs(realizedPnL).toFixed(1)}` : `SL -$${Math.abs(realizedPnL).toFixed(1)}`,
      }),
    ];

    events.push({
      type: "transaction",
      transaction: {
        id: closingTradeId,
        symbol: settings.pair,
        side: realizedPnL >= 0 ? "sell" : "stop-loss",
        status: "completed",
        quantity: nextRuntime.openPosition.quantity,
        price: marketPrice,
        profit: realizedPnL,
        amount: nextRuntime.openPosition.quantity * marketPrice,
        investedAmount: nextRuntime.openPosition.invested,
        source: settings.botSelection,
        timestamp: new Date().toISOString(),
        description: `${realizedPnL >= 0 ? "Take profit" : "Risk stop"} close on ${market.label} at ${signedPnl}.`,
      },
    });

    nextRuntime.openPosition = null;
    nextRuntime.activeTradeStatus = `Last close ${signedPnl}`;
  };

  if (!nextRuntime.openPosition) {
    nextRuntime.state = "scanning";
    const shouldAttemptBuy =
      rsi <= profile.entryRsi ||
      confidence >= profile.confidenceBias + 8 ||
      Math.random() < profile.executionChance / 3;

    if (shouldAttemptBuy) {
      const success = confidence >= profile.confidenceBias || Math.random() < profile.executionChance;
      const executableTradeAmount = getExecutableTradeAmount();
      nextRuntime.logs.push(
        createExecutionLog(`BUY ATTEMPT confidence ${confidence.toFixed(1)}%.`, success ? "warn" : "info")
      );

      if (success && executableTradeAmount > 0) {
        executeBuy("base", executableTradeAmount);
      } else {
        nextRuntime.state = "waiting";
        nextRuntime.logs.push(
          createExecutionLog(
            success
              ? "BUY ATTEMPT delayed because no portfolio balance is currently available for execution."
              : "BUY ATTEMPT rejected by execution filters. Waiting for cleaner entry.",
            "warn"
          )
        );
        events.push({
          type: "transaction",
          transaction: {
            id: crypto.randomUUID(),
            symbol: settings.pair,
            side: "rejected-attempt",
            status: "completed",
            quantity: null,
            price: marketPrice,
            profit: null,
            amount: settings.tradeAmount,
            investedAmount: 0,
            source: settings.botSelection,
            timestamp: new Date().toISOString(),
            description: `Buy attempt rejected on ${market.label}.`,
          },
        });
      }
    } else {
      nextRuntime.state = "waiting";
      nextRuntime.logs.push(
        createExecutionLog("No valid entry this cycle. Bot remains in standby scan mode.", "info")
      );
      events.push({
        type: "transaction",
        transaction: {
          id: crypto.randomUUID(),
          symbol: settings.pair,
          side: "standby",
          status: "completed",
          quantity: null,
          price: marketPrice,
          profit: null,
          amount: 0,
          investedAmount: 0,
          source: settings.botSelection,
          timestamp: new Date().toISOString(),
          description: `No valid entry found on ${market.label}. Bot remained in standby.`,
        },
      });
    }
  } else {
    const positionValue = nextRuntime.openPosition.quantity * marketPrice;
    const unrealizedPnL = positionValue - nextRuntime.openPosition.invested;
    const unrealizedPercent =
      nextRuntime.openPosition.invested > 0
        ? (unrealizedPnL / nextRuntime.openPosition.invested) * 100
        : 0;

    nextRuntime.logs.push(
      createExecutionLog(
        `Monitoring price movement... active position ${unrealizedPercent >= 0 ? "+" : ""}${unrealizedPercent.toFixed(2)}%.`,
        unrealizedPercent >= 0 ? "success" : "warn"
      )
    );
    events.push({
      type: "transaction",
      transaction: {
        id: crypto.randomUUID(),
        symbol: settings.pair,
        side: "monitor",
        status: "completed",
        quantity: nextRuntime.openPosition.quantity,
        price: marketPrice,
        profit: unrealizedPnL,
        amount: positionValue,
        investedAmount: nextRuntime.openPosition.invested,
        source: settings.botSelection,
        timestamp: new Date().toISOString(),
        description: `Monitoring ${market.label} position at ${unrealizedPercent >= 0 ? "+" : ""}${unrealizedPercent.toFixed(2)}%.`,
      },
    });

    if (
      unrealizedPercent <= -profile.dcaTriggerPercent &&
      nextRuntime.openPosition.dcaCount < profile.maxDcaOrders
    ) {
      const executableTradeAmount = getExecutableTradeAmount();
      if (executableTradeAmount > 0) {
        executeBuy("dca", executableTradeAmount);
      } else {
        nextRuntime.state = "monitoring";
        nextRuntime.logs.push(
          createExecutionLog(
            "DCA ENTRY delayed because the remaining portfolio balance is fully allocated.",
            "warn"
          )
        );
      }
    } else if (unrealizedPercent >= profile.takeProfitPercent || confidence >= 84) {
      closePosition("take-profit", "success");
    } else if (unrealizedPercent <= -profile.stopLossPercent) {
      closePosition("risk-stop", "error");
    } else {
      nextRuntime.state = "monitoring";
    }
  }

  return {
    runtime: syncRuntimeWithMarket({
      runtime: {
        ...nextRuntime,
        equity: Number((nextRuntime.currentBalance + (nextRuntime.totalProfitLoss - nextRuntime.realizedProfitLoss)).toFixed(2)),
      },
      marketPrice,
    }),
    events,
  };
};

export const getStateMeta = (state) => BOT_STATE_META[state] || BOT_STATE_META.stopped;

export const getCycleCountdownLabel = (runtime) =>
  runtime.nextTradeAt
    ? `Next Trade Cycle: ${formatCountdown(
        Math.max(0, Math.floor((runtime.nextTradeAt - Date.now()) / 1000))
      )}`
    : "Next Trade Cycle: 00:00:00";
